/**
 * ThemeMenu — appearance control for the app shell (redesign follow-up).
 * Lets the user pick a mode (Light / Dark / System) and an accent theme; writes
 * data-mode / data-theme onto <html> and persists to localStorage (gd:mode /
 * gd:theme) — the same keys main.tsx reads at boot. "System" is stored as an
 * explicit 'system' value (and follows prefers-color-scheme live); a MISSING
 * key means "never chose" and boots dark, the app default.
 */
import './ThemeMenu.css';
import { useEffect, useRef, useState } from 'react';
import { Icon, IconButton, type IconName } from './ui';

type Mode = 'light' | 'dark' | 'system';

const MODES: { id: Mode; label: string; icon: IconName }[] = [
  { id: 'light', label: 'Light', icon: 'sun' },
  { id: 'dark', label: 'Dark', icon: 'moon' },
  { id: 'system', label: 'System', icon: 'theme' },
];

const THEMES: { id: string; label: string; accent: string }[] = [
  { id: 'obsidian', label: 'Obsidian Ember', accent: '#e0a64d' },
  { id: 'leather', label: 'Tooled Leather', accent: '#e3b25a' },
  { id: 'amethyst', label: 'Arcane Amethyst', accent: '#9d6bff' },
  { id: 'verdant', label: 'Verdant Hollow', accent: '#3fb98f' },
  { id: 'blood', label: 'Blood & Brass', accent: '#c0392b' },
];

function systemMode(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export default function ThemeMenu() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>(() => {
    try {
      // Missing key = never chose = the app's dark default (see main.tsx).
      return (localStorage.getItem('gd:mode') as Mode) || 'dark';
    } catch {
      return 'dark';
    }
  });
  const [theme, setTheme] = useState<string>(() => {
    try {
      return localStorage.getItem('gd:theme') || 'obsidian';
    } catch {
      return 'obsidian';
    }
  });
  const rootRef = useRef<HTMLDivElement>(null);

  const applyMode = (m: Mode) => {
    setMode(m);
    const el = document.documentElement;
    try {
      // 'system' is persisted explicitly — a missing key must keep meaning
      // "never chose", which boots dark rather than following the OS.
      localStorage.setItem('gd:mode', m);
    } catch {
      /* ignore */
    }
    el.setAttribute('data-mode', m === 'system' ? systemMode() : m);
  };

  const applyTheme = (t: string) => {
    setTheme(t);
    try {
      localStorage.setItem('gd:theme', t);
    } catch {
      /* ignore */
    }
    document.documentElement.setAttribute('data-theme', t);
  };

  // Follow the OS while in System mode.
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => document.documentElement.setAttribute('data-mode', mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [mode]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="thememenu" ref={rootRef}>
      <IconButton
        icon="theme"
        label="Appearance"
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      />
      {open && (
        <div className="thememenu__pop" role="menu">
          <div className="thememenu__label">Mode</div>
          <div className="thememenu__seg">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`thememenu__seg-btn${mode === m.id ? ' is-active' : ''}`}
                onClick={() => applyMode(m.id)}
              >
                <Icon name={m.icon} size={14} />
                {m.label}
              </button>
            ))}
          </div>

          <div className="thememenu__label">Theme</div>
          <div className="thememenu__themes">
            {THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`thememenu__theme${theme === t.id ? ' is-active' : ''}`}
                onClick={() => applyTheme(t.id)}
              >
                <span className="thememenu__swatch" style={{ background: t.accent }} />
                {t.label}
                {theme === t.id && <Icon name="check" size={14} className="thememenu__check" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
